import useTheme from '@base/useTheme';
import { Button } from '@components/button';
import { SelectInput } from '@components/form';
import { PageLayout } from '@components/page/PageLayout';
import { useFoldersActions } from '@data/folder/folderActions';
import { useImportJobRouteParams } from '@data/hooks/importJobsRouteParams';
import { useDetectDimensions, useImportJobs } from '@data/importJob/importJobActions';
import { importJobsApi } from '@data/rest/api';
import { useGlobalLoadingContext } from '@features/loading/globalLoadingContext';
import { useNavigateWithParams } from '@hooks/useNavigateWithParams';
import { useI18nContext } from '@i18n/i18n-react';
import { pagePath } from '@pages/pagePaths';
import { handleApiErrorMessage } from '@util/message';
import React, { useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { RiUploadCloud2Line } from 'react-icons/ri';

export default function ImportJobNew() {
    const { LL } = useI18nContext();
    const theme = useTheme();
    const navigate = useNavigateWithParams();
    const importJobRouteParams = useImportJobRouteParams();
    const importJobs = useImportJobs().data ?? [];
    const detectDimensions = useDetectDimensions();
    const { refreshAllFolders } = useFoldersActions();
    const { globalLoadingWrapped } = useGlobalLoadingContext();

    const onDrop = React.useCallback(
        async (acceptedFiles: File[]) => {
            const createdJobs = await globalLoadingWrapped(async () => {
                try {
                    // create import jobs from uploaded file
                    return await importJobsApi.importFile(
                        acceptedFiles[0],
                        importJobRouteParams.replaceImportJobId?.toString() ?? undefined
                    );
                } catch (err) {
                    await handleApiErrorMessage(err, LL.ERROR_IMPORTING_FILE());
                }
            });
            if (createdJobs !== undefined && createdJobs.length === 1) {
                refreshAllFolders();
                const createdJobId = createdJobs[0].id;
                navigate(`${pagePath.Import}/job/${createdJobId}`);
                await detectDimensions.mutateAsync(createdJobId);
            } else if (createdJobs !== undefined && createdJobs.length > 0) {
                navigate(`${pagePath.Import}`);
            }
        },
        [
            LL,
            detectDimensions,
            globalLoadingWrapped,
            importJobRouteParams.replaceImportJobId,
            navigate,
            refreshAllFolders,
        ]
    );

    const handleBrowseClick = () => {
        inputRef.current?.click();
    };

    const { getRootProps, getInputProps, isDragAccept, inputRef } = useDropzone({
        onDrop,
        noClick: true,
    });

    const dropzoneStyle = useMemo(() => {
        if (isDragAccept) {
            return { backgroundColor: theme.colors.neutral.w100 };
        } else {
            return {};
        }
    }, [isDragAccept, theme]);

    const previousJobsOptions = [
        {
            label: 'None',
            value: null,
        },
        ...importJobs.map((job) => ({
            label: job.title,
            value: job.id,
        })),
    ];

    const title = (
        <Button
            $type="quaternary"
            className="right"
            title={LL.CANCEL()}
            onClick={() => {
                navigate(`${pagePath.Import}`);
            }}
        />
    );

    const actions =
        importJobs.length > 0 ? (
            <SelectInput
                minWidth={250}
                options={previousJobsOptions}
                placeholder={LL.CHOOSE_JOB_TO_REPLACE()}
                value={importJobRouteParams.replaceImportJobId}
                onChange={async (selectedId) => {
                    if (selectedId === null) {
                        navigate(pagePath.Import);
                    } else {
                        navigate(`${pagePath.Import}/replace/${selectedId}`);
                    }
                }}
            />
        ) : null;

    return (
        <PageLayout title={title} actions={actions}>
            <div className="fs-import-container">
                <div className="import-new">
                    {importJobs.length === 0 && (
                        <div className="import-new-intro">
                            <h2>{LL.WELCOME_TO_IMPORT()}</h2>
                            <p>{LL.IMPORT_SPREADSHEETS()}</p>
                        </div>
                    )}
                    <div className="import-new-dropfield" {...getRootProps({ style: dropzoneStyle })}>
                        <input type="file" ref={inputRef} style={{ display: 'none' }} {...getInputProps()} />
                        <RiUploadCloud2Line />
                        <p>{LL.DRAG_FILE_HERE()}</p>
                    </div>
                    <div className="import-new-browse">
                        <span className="import-new-or">
                            <span className="line"></span>
                            {LL.OR()}
                            <span className="line"></span>
                        </span>
                        <Button title={LL.BROWSE()} onClick={handleBrowseClick} />
                    </div>
                </div>
            </div>
        </PageLayout>
    );
}
